import React, { useState } from 'react';
import { NoticeModel } from '../../../../models';
import { Bell, Search, Pin, ChevronRight } from 'lucide-react';
import AdminPageHeader from '../../frame/RightPanelHeader';
import { db } from '../../../../firebase';
import { doc, updateDoc, increment, setDoc } from 'firebase/firestore';

interface HQNoticeViewProps {
    notices: NoticeModel[];
    memberId: string;
    readNoticeIds: string[];
}

const HQNoticeView: React.FC<HQNoticeViewProps> = ({ notices, memberId, readNoticeIds }) => {
    const [selectedNotice, setSelectedNotice] = useState<NoticeModel | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredNotices = notices.filter(n =>
        n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.content.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return b.createdAt - a.createdAt;
    });

    const handleView = async (notice: NoticeModel) => {
        setSelectedNotice(notice);
        // Track view
        try {
            await updateDoc(doc(db, 'notices', notice.id), {
                viewCount: increment(1)
            });
            // Mark as read for this user
            await setDoc(doc(db, 'members', memberId, 'read_notices', notice.id), {
                readAt: Date.now()
            }, { merge: true });
        } catch (e) {
            console.error(e);
        }
    };

    if (selectedNotice) {
        return (
            <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full animate-in fade-in duration-500">
                <div className="bg-white border-b border-slate-200 px-8 py-5 flex items-center justify-between sticky top-0 z-10 shrink-0">
                    <button
                        onClick={() => setSelectedNotice(null)}
                        className="flex items-center text-slate-500 hover:text-indigo-600 transition-colors gap-2 font-bold"
                    >
                        <div className="bg-slate-100 p-2 rounded-full">
                            <ChevronRight size={18} className="rotate-180" />
                        </div>
                        목록으로 돌아가기
                    </button>
                    <span className="text-xs font-bold text-slate-400">본사 공지사항 상세</span>
                </div>

                <div className="flex-1 overflow-y-auto p-8">
                    <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                        <div className="p-8 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center gap-3 mb-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${selectedNotice.category === '중요' ? 'bg-rose-100 text-rose-700' :
                                    selectedNotice.category === '지점' ? 'bg-amber-100 text-amber-700' :
                                        'bg-indigo-100 text-indigo-700'
                                    }`}>
                                    {selectedNotice.category}
                                </span>
                                <span className="text-slate-400 text-xs font-medium">
                                    {new Date(selectedNotice.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                            <h1 className="text-2xl font-black text-slate-800 leading-tight">
                                {selectedNotice.title}
                            </h1>
                            <div className="mt-4 flex items-center gap-4 text-xs font-bold text-slate-500">
                                <span>작성자: {selectedNotice.authorName}</span>
                                <span>•</span>
                                <span>조회수: {selectedNotice.viewCount}</span>
                            </div>
                        </div>
                        <div className="p-10 min-h-[400px]">
                            <div
                                className="prose prose-slate max-w-none prose-headings:font-bold prose-a:text-indigo-600"
                                dangerouslySetInnerHTML={{ __html: selectedNotice.content }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full animate-in fade-in duration-500">
            <AdminPageHeader
                title="본사 공지사항"
                icon={Bell}
                subtitle="최고관리자로부터 수신된 공지사항을 확인합니다."
            />

            <div className="px-8 py-5 border-b border-slate-200 bg-white shrink-0 z-10 w-full">
                <div className="max-w-xl relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                    <input
                        type="text"
                        placeholder="공지사항 검색..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-4 py-3 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all shadow-sm"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-5xl mx-auto space-y-3">
                    {filteredNotices.length > 0 ? filteredNotices.map((notice) => {
                        const isRead = readNoticeIds.includes(notice.id);
                        return (
                            <div
                                key={notice.id}
                                onClick={() => handleView(notice)}
                                className={`bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-all cursor-pointer group flex items-center justify-between gap-6 ${notice.isPinned ? 'ring-2 ring-indigo-500/20' : ''}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                        {!isRead && (
                                            <span className="bg-rose-500 w-2 h-2 rounded-full ring-2 ring-rose-100 mr-1" />
                                        )}
                                        {notice.isPinned && (
                                            <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-[10px] font-bold flex items-center">
                                                <Pin size={10} className="mr-1 fill-current" />
                                                고정됨
                                            </span>
                                        )}
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${notice.category === '중요' ? 'bg-rose-100 text-rose-700' :
                                            notice.category === '지점' ? 'bg-amber-100 text-amber-700' :
                                                'bg-slate-100 text-slate-600'
                                            }`}>
                                            {notice.category}
                                        </span>
                                        <span className="text-slate-400 text-[11px] font-medium">
                                            {new Date(notice.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <h3 className={`text-base text-slate-800 truncate group-hover:text-indigo-600 transition-colors ${!isRead ? 'font-black' : 'font-medium text-slate-600'}`}>
                                        {notice.title}
                                    </h3>
                                    <div className="text-slate-500 text-xs line-clamp-1 mt-1 text-ellipsis overflow-hidden"
                                        dangerouslySetInnerHTML={{ __html: notice.content.replace(/<[^>]*>/g, ' ') }}
                                    />
                                </div>
                                <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 text-slate-300 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all">
                                    <ChevronRight size={20} />
                                </div>
                            </div>
                        )
                    }) : (
                        <div className="py-20 text-center">
                            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-300">
                                <Bell size={32} />
                            </div>
                            <h3 className="text-slate-500 font-bold">등록된 공지사항이 없습니다.</h3>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HQNoticeView;
